// Cancelling pending human-in-the-loop (HITL) tool calls when the user sends a
// message instead of answering the widget.
//
// A HITL tool call (e.g. `ask_user_question`) pauses the run
// awaiting a user answer. If the user sends a normal message while one is open,
// the call must NOT be left unanswered — a stranded call corrupts the thread
// (Gemini 400 on the next turn, or a reload crash / empty-args widget; see the
// CopilotKit upstream issue referenced in ChoicePrompt). Instead of routing the
// text to the widget, we append a "dismissed" tool result for each pending call
// and let it ride to the backend in the SAME run as the user's message.

// Tool names whose calls pause the run awaiting a user answer.
const HITL_TOOL_NAMES = new Set(['ask_user_question'])

// Tool-result content written for a HITL call the user dismissed by sending a
// message instead of answering. ChoicePrompt renders this as a "dismissed"
// state, and the agent should read it as "no option was selected".
export const HITL_CANCELLED_RESULT =
    'The user dismissed this question without selecting an option and sent a new message instead.'

// Minimal structural view of the agent's messages (assignable from AG-UI's
// `Message[]`) so this stays free of framework type imports.
interface MessageLike {
    role: string
    toolCalls?: { id: string; function: { name: string } }[]
    toolCallId?: string
}

// IDs of HITL tool calls that have no tool-result message yet — i.e. are paused
// awaiting an answer. Reads the live message history (rather than tracking a
// single pending responder), so it covers however many are open.
export function pendingHitlToolCallIds(
    messages: readonly MessageLike[]
): string[] {
    const answered = new Set<string>()
    for (const m of messages) {
        if (m.role === 'tool' && m.toolCallId) answered.add(m.toolCallId)
    }
    const pending: string[] = []
    for (const m of messages) {
        if (m.role !== 'assistant' || !m.toolCalls) continue
        for (const tc of m.toolCalls) {
            if (HITL_TOOL_NAMES.has(tc.function.name) && !answered.has(tc.id)) {
                pending.push(tc.id)
            }
        }
    }
    return pending
}
